import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useEmail } from '../../hooks/useEmail';
import {ReactComponent as Logo} from '../../assets/images/ugcan-logo.svg';

const ForgotPassword = ({ closeModal }) => {
  const navigate = useNavigate();
  const { sendPasswordResetEmail } = useEmail();
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const buttonClass = `m-2 p-2 h-10 w-fit 
                      bg-transparent text-primary-900 hover:text-white hover:bg-primary-900 
                      border-2 rounded-lg 
                      text-base font-bold tracking-widest normal-case items-center
                      rounded-full shadow-md transition`;
  
  const fieldClass = "w-full bg-tertiary-400 m-1 p-2 text-sm font-nunito placeholder:text-gray-500 rounded";

  const handleClose = () => {
    if (typeof closeModal === 'function') {
      closeModal();
    } else {
      navigate('/login');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Email validation
    const isEmailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    if (!email) {
      setError("Email is required.");
      return;
    }
    if (!isEmailValid) {
      setError("Please enter a valid email address.");
      return;
    }

    setLoading(true);
    setError('');

    try {
      // First, request password reset from backend
      const response = await axios.post('/auth/users/reset_password/', { email });
      
      if (response.status === 200) {
        // If backend successfully processes the request, send email via Zoho
        try {
          // Generate a reset token (this should come from backend in real implementation)
          const resetToken = `reset-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
          
          await sendPasswordResetEmail(email, resetToken);
          setStatus('Success');
          setError('✅ Password reset email sent! Please check your inbox and follow the instructions to reset your password.');
        } catch (emailError) {
          console.warn('Email service failed:', emailError);
          // Still show success if backend processed the request
          setStatus('Success');
          setError('✅ Password reset request processed. Please check your email for reset instructions.');
        }
      }
    } catch (err) {
      console.error('Password reset error:', err);
      setStatus('Error');
      
      if (err.response && err.response.data) {
        const errorData = err.response.data;
        if (errorData.detail) {
          setError(`❌ ${errorData.detail}`);
        } else if (errorData.email) {
          setError(`❌ ${errorData.email[0]}`);
        } else {
          setError('❌ Password reset request failed. Please try again.');
        }
      } else if (err.request) {
        setError('❌ Network error. Please check your connection and try again.');
      } else {
        setError('❌ An unexpected error occurred. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
      <div className="modal-content w-full max-w-sm p-6 bg-primary-100 white rounded-lg shadow-lg space-y-4 relative">
        <div className="w-full max-w-sm p-6 rounded-lg shadow-lg space-y-4 bg-tertiary-600 relative">
          {/* Close button */}
          <button
            onClick={handleClose}
            className={`${buttonClass} absolute top-2 right-2 w-8 h-8 flex items-center justify-center z-20`}
            aria-label="Close"
            type="button"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          {/* Logo */}
          <div className="flex justify-center items-center h-full">
            <Logo />
          </div>

          {/* Header */}
          <div className="text-left bg-gradient-to-br from-white to-primary-200 border border-blue-100 p-5 rounded-xl shadow-sm space-y-3">
            <h3 className="text-2xl font-bold text-blue-800">🔐 Forgot Password?</h3>
            <p className="text-gray-700 text-base leading-relaxed">
              No worries! Enter your email address and we'll send you a link to reset your password.
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="w-full text-center">
            <div className="flex flex-col text-center justify-center">
              <input
                type="email"
                id="email"
                name="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email address"
                className={`${fieldClass}`}
                required
                autoComplete="email"
                disabled={loading}
              />
            </div>

            {/* Error/Success Message */}
            {error && (
              <div className="m-4">
                {status === 'Error' && <p className="text-red-500 text-center">❌ Password Reset Failed</p>}
                <p className={`text-center ${status === 'Success' ? 'text-green-600' : 'text-red-500'}`}>
                  {error}
                </p>
              </div>
            )}

            {/* Action Buttons */}
            {status === 'Success' ? (
              <div className="flex flex-col text-center w-full">
                <button
                  type="button"
                  className={`${buttonClass} w-full justify-center`}
                  onClick={handleClose}
                >
                  Back to Login
                </button>
              </div>
            ) : (
              <div className="flex flex-col text-center w-full">
                <button
                  type="submit"
                  className={`${buttonClass} w-full justify-center`}
                  disabled={loading}
                >
                  {loading ? 'Sending...' : 'Send Reset Link'}
                </button>
                <button
                  type="button"
                  onClick={handleClose}
                  className={`${buttonClass} w-full justify-center`}
                  disabled={loading}
                >
                  Cancel
                </button>
              </div>
            )}
          </form>

          {/* Footer */}
          <div className="text-center">
            <p className="text-sm text-gray-500">
              Remember your password?{' '}
              <span 
                className="text-blue-600 cursor-pointer hover:underline"
                onClick={handleClose}
              >
                Back to Login
              </span>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;

